import { z } from 'zod';
/**
 * Creates a Zod schema for a Flavor type that validates the underlying type
 * and brands the output with the appropriate Flavor type.
 *
 * @param baseSchema - The Zod schema for the underlying type T
 * @returns A Zod schema that outputs Flavor<T, FlavorT>
 *
 * @example
 * ```typescript
 * // Define a branded type
 * type UserId = Flavor<string, 'UserId'>;
 *
 * // Create a schema for it
 * const UserIdSchema = flavorSchema<string, 'UserId'>(z.string().uuid());
 *
 * // Use the schema
 * const userId = UserIdSchema.parse('123e4567-e89b-12d3-a456-426614174000');
 * // userId is typed as UserId, not just string
 * ```
 */
export function flavorSchema(baseSchema) {
    return baseSchema.transform((val) => val);
}
/**
 * Creates a branded string schema with common string validations
 *
 * @example
 * ```typescript
 * type OrganizationId = Flavor<string, 'OrganizationId'>;
 * const OrganizationIdSchema = brandedString<'OrganizationId'>()
 *   .uuid('Invalid organization ID format');
 * ```
 */
export function brandedString() {
    return z.string().transform((val) => val);
}
/**
 * Creates a branded number schema with common number validations
 *
 * @example
 * ```typescript
 * type AccountBalance = Flavor<number, 'AccountBalance'>;
 * const AccountBalanceSchema = brandedNumber<'AccountBalance'>()
 *   .min(0, 'Balance cannot be negative');
 * ```
 */
export function brandedNumber() {
    return z.number().transform((val) => val);
}
/**
 * Pre-built schema for common UUID-based branded types
 */
export function brandedUuid(message) {
    return z.uuid(message).transform((val) => val);
}
//# sourceMappingURL=FlavorSchema.js.map